import { AppDispatch } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { SNACKBAR_DEFAULT_CLOSE_TIME } from 'app/components/Alerts/constants';
import { BaseRelease, ReleaseType, TuneCoreRelease } from 'types/releases';
import {
  soundcloudReleaseExportCreatedAction,
  tuneCoreReleaseExportCreatedAction,
} from 'app/store/Mixpanel/slice';
import { ReleaseExportStatusUpdatePayload } from 'types/websocket.types';
import { removeFromDownloadList } from 'app/store/Shares/slice';
import { Alert, AlertIcon } from 'app/store/Alerts/slice.types';
import {
  setReleaseExport,
  setTuneCoreFailureDialog,
  setTuneCoreSuccessDialog,
  updateLatestExportAction,
  updateReleaseStatus,
} from 'app/store/Releases/slice';
import { dateToTime } from 'app/utils/utils';
import { fetchReleaseEffect } from 'app/store/Releases/effects';

type ReleaseExportStatusUpdatePayloadWithIsAudioUploadSupported = ReleaseExportStatusUpdatePayload & {
  isAudioUploadSupported?: boolean;
};

const handleLabelReleaseExport = (
  payload: ReleaseExportStatusUpdatePayloadWithIsAudioUploadSupported,
  releaseGuid: string | undefined,
  dispatch: AppDispatch
) => {
  const { parentGuid, status } = payload;

  if (releaseGuid === parentGuid && status === 'FAIL') {
    dispatch(
      addAlertAction({
        id: parentGuid,
        message: 'Export failed, please try again',
        autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
      })
    );
  }
};

const handleLabelV2ReleaseExport = async (
  payload: ReleaseExportStatusUpdatePayloadWithIsAudioUploadSupported,
  _: string | undefined,
  dispatch: AppDispatch
) => {
  const { parentGuid, status, isAudioUploadSupported } = payload;

  switch (status) {
    case 'PROCESSING':
      dispatch(updateReleaseStatus('Processing'));
      break;
    case 'FAIL':
      dispatch(
        addAlertAction({
          id: parentGuid,
          message: 'Export failed, please try again',
          autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
        })
      );
      break;
    case 'SUCCESS':
      dispatch(
        addAlertAction({
          id: parentGuid,
          message: isAudioUploadSupported
            ? 'Export to Connex successfully finished.'
            : 'Release information sent but note that this recipient does not yet support Audio in Connex. Contact your A&R for more info',
          statusIcon: AlertIcon.SUCCESS,
          autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
        })
      );
      dispatch(setReleaseExport(undefined));
      break;
    default:
  }
};

const handleSoundCloudReleaseExport = (
  payload: ReleaseExportStatusUpdatePayloadWithIsAudioUploadSupported,
  releaseGuid: string | undefined,
  dispatch: AppDispatch,
  audioCount?: number
) => {
  const { parentGuid, status } = payload;

  if (releaseGuid !== parentGuid) {
    return;
  }

  if (status === 'FAIL') {
    dispatch(
      addAlertAction({
        id: parentGuid,
        message: 'Export failed, please try again',
        autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
      })
    );
  } else if (status === 'SUCCESS') {
    dispatch(removeFromDownloadList(parentGuid));
    dispatch(
      addAlertAction({
        id: parentGuid,
        message:
          'Export to SoundCloud successfully finished. Please check your SoundCloud account',
        statusIcon: AlertIcon.SUCCESS,
        autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
      })
    );
    dispatch(
      soundcloudReleaseExportCreatedAction({
        releaseId: releaseGuid as string,
        audioCount,
      })
    );
  }
};

const handleTuneCoreReleaseExport = async (
  payload: ReleaseExportStatusUpdatePayloadWithIsAudioUploadSupported,
  releaseGuid: string | undefined,
  dispatch: AppDispatch,
  audioCount?: number
) => {
  const { parentGuid, status } = payload;
  const exportItem = payload as BaseRelease['latestExportShipment'];

  if (['SUCCESS', 'FAIL'].includes(status as string)) {
    const response = await dispatch(
      fetchReleaseEffect({ guid: exportItem?.parentGuid || '', silent: true })
    ).unwrap();

    const isSuccess = Boolean(
      (response as TuneCoreRelease)?.latestExport?.tuneCore?.callbackResponse
        ?.tuneCoreReleaseUrl
    );

    const action = isSuccess
      ? setTuneCoreSuccessDialog
      : setTuneCoreFailureDialog;

    dispatch(action({ guid: parentGuid }));
    dispatch(addAlertAction({ id: parentGuid, autoHideDuration: 1 } as Alert));
    dispatch(
      tuneCoreReleaseExportCreatedAction({
        releaseId: releaseGuid as string,
        audioCount,
      })
    );
  } else {
    dispatch(
      updateLatestExportAction({
        export: exportItem,
        date: dateToTime(new Date()),
      })
    );
  }

  dispatch(
    updateLatestExportAction({
      export: exportItem,
      date: dateToTime(new Date()),
    })
  );
};

export const handleReleaseExportsByType = {
  [ReleaseType.LABEL]: handleLabelReleaseExport,
  [ReleaseType.SOUNDCLOUD]: handleSoundCloudReleaseExport,
  [ReleaseType.TUNECORE]: handleTuneCoreReleaseExport,
  [ReleaseType.LABEL_V2]: handleLabelV2ReleaseExport,
};
